import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './WarehouseProductivity.css';
import { logout, getUserInfo, getAuthToken, filterWarehouseData } from '../utils/auth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const API_URL = `${API_BASE}/warehouse`;

const orderTypeLabels = { A: 'Assemblies', C: 'Customer', T: 'Transfers' };

const todayStr     = () => new Date().toISOString().split('T')[0];
const firstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

function WarehouseProductivityHybrid() {
  const navigate = useNavigate();
  const userInfo = getUserInfo();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [countries, setCountries] = useState([]);
  const [orderTypes, setOrderTypes] = useState([]);
  const [startDate, setStartDate] = useState(firstOfMonth());
  const [endDate, setEndDate] = useState(todayStr());
  const [country, setCountry] = useState('All');
  const [orderType, setOrderType] = useState('All');
  const [excludeHQ, setExcludeHQ] = useState(false);
  const [excludeCarriers, setExcludeCarriers] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${getAuthToken()}` });

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [cRes, otRes] = await Promise.all([
          axios.get(`${API_URL}/hybrid/countries`, { headers: headers() }),
          axios.get(`${API_URL}/hybrid/order-types`, { headers: headers() }),
        ]);
        setCountries(cRes.data.countries || []);
        setOrderTypes(otRes.data.orderTypes || []);
      } catch (err) {
        if (err.response?.status === 401) { logout(); window.location.href = '/login'; }
      }
    };
    loadFilters();
    fetchData(firstOfMonth(), todayStr(), 'All', 'All', false, false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async (sd, ed, ctry, ot, exclHQ, excl) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (sd)   params.append('startDate', sd);
      if (ed)   params.append('endDate', ed);
      if (ctry && ctry !== 'All') params.append('country', ctry);
      if (ot   && ot   !== 'All') params.append('orderType', ot);
      if (exclHQ) params.append('excludeHQ', 'true');
      if (excl)   params.append('excludeCarriers', 'true');

      const res = await axios.get(`${API_URL}/hybrid/summary?${params}`, { headers: headers() });
      setData(res.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 401) { logout(); window.location.href = '/login'; }
      else setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => fetchData(startDate, endDate, country, orderType, excludeHQ, excludeCarriers);

  const handleClear = () => {
    const sd = firstOfMonth();
    const ed = todayStr();
    setStartDate(sd); setEndDate(ed);
    setCountry('All'); setOrderType('All');
    setExcludeHQ(false); setExcludeCarriers(false);
    fetchData(sd, ed, 'All', 'All', false, false);
  };

  const handleLogout = () => { logout(); window.location.href = '/login'; };

  const fmt     = (n) => Number(n).toLocaleString('en-US');
  const fmtDec  = (n) => Number(n).toFixed(1);
  const fmtDec4 = (n) => Number(n).toFixed(4);

  const warehouses = data ? filterWarehouseData(data.warehouses, 'warehouse') : [];

  return (
    <div className="wp-container">
      <header className="wp-header">
        <div className="wp-header-top">
          <div>
            <h1>Warehouse Performance Stats</h1>
            <h2>Open &amp; Invoiced Summary</h2>
          </div>
          <div className="wp-header-actions">
            {userInfo && <span className="wp-welcome">Welcome, {userInfo.fullName || userInfo.username}</span>}
            <button className="wp-back-btn" onClick={() => navigate('/')}>← Back to Home</button>
            <button className="wp-logout-btn" onClick={handleLogout}>Logout</button>
          </div>
        </div>
      </header>

      <div className="wp-filters">
        <div className="wp-filter-group">
          <label>Start Date</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </div>
        <div className="wp-filter-group">
          <label>End Date</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </div>
        <div className="wp-filter-group">
          <label>Country</label>
          <select value={country} onChange={e => setCountry(e.target.value)}>
            <option value="All">All</option>
            {countries.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="wp-filter-group">
          <label>Order Type</label>
          <select value={orderType} onChange={e => setOrderType(e.target.value)}>
            <option value="All">All</option>
            {orderTypes.map(t => <option key={t} value={t}>{orderTypeLabels[t] || t}</option>)}
          </select>
        </div>
        <div className="wp-filter-group wp-checkbox-group">
          <label>
            <input type="checkbox" checked={excludeHQ} onChange={e => setExcludeHQ(e.target.checked)} />
            Exclude Customers: HQ
          </label>
        </div>
        <div className="wp-filter-group wp-checkbox-group">
          <label>
            <input type="checkbox" checked={excludeCarriers} onChange={e => setExcludeCarriers(e.target.checked)} />
            Exclude Carriers: <span className="carrier-tip" data-tip="Hold for Instructions">Z</span>
            {'/'}<span className="carrier-tip" data-tip="Will Call">WC</span>
            {'/'}<span className="carrier-tip" data-tip="Hold For Stock Truck">HFSK</span>
          </label>
        </div>
        <button className="wp-apply-btn" onClick={handleApply}>Apply Filters</button>
        <button className="wp-clear-btn" onClick={handleClear}>Clear Filters</button>
      </div>

      {loading && <div className="wp-loading">Loading...</div>}
      {error   && <div className="wp-error">{error}</div>}

      {!loading && data && (
        <div className="wp-content">
          <div className="wp-summary-bar">
            <span className="wp-date-range">{data.dateRange}</span>
            <span style={{ color: '#aaaaaa', fontSize: '13px' }}>
              Working days in range: <strong style={{ color: '#ffffff' }}>{data.workingDays}</strong>
            </span>
          </div>

          <div className="wp-hybrid-wrap">
            <table className="wp-hybrid-table">
              <thead>
                <tr>
                  <th className="wp-th-warehouse">Warehouse</th>
                  <th>
                    Total Open Orders<br />
                    <span className="wp-col-sub">as of now</span>
                  </th>
                  <th>
                    Tot # Invoiced<br />
                    <span className="wp-col-sub">In Range</span>
                    <span className="col-tip" data-tip="Number of orders invoiced in the range, could include orders from prior months">?</span>
                  </th>
                  <th>
                    Avg Created / Day<br />
                    <span className="wp-col-sub">÷ working days</span>
                    <span className="col-tip" data-tip="Daily avg orders created during this time period">?</span>
                  </th>
                  <th>
                    Avg Invoiced / Day<br />
                    <span className="wp-col-sub">÷ working days</span>
                    <span className="col-tip" data-tip="Daily avg orders invoiced during this time period">?</span>
                  </th>
                  <th>
                    Avg Days<br />
                    Gen → Assign
                    <span className="col-tip" data-tip="Avg number of days between pick ticket generation and assignment">?</span>
                  </th>
                  <th>
                    Avg Days<br />
                    Gen → Invoice
                    <span className="col-tip" data-tip="Avg number of days between pick ticket generation and invoice">?</span>
                  </th>
                  <th className="wp-th-alert">
                    Open &gt; 2 Days<br />
                    <span className="wp-col-sub">as of now</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {warehouses.map(w => (
                  <tr key={w.warehouse}>
                    <td className="wp-td-warehouse">{w.warehouse}</td>
                    <td>{fmt(w.total_open)}</td>
                    <td>{fmt(w.total_invoiced)}</td>
                    <td>{fmtDec(w.avg_created_per_day)}</td>
                    <td>{fmtDec(w.avg_invoiced_per_day)}</td>
                    <td>{fmtDec4(w.avg_days_gen_to_assign)}</td>
                    <td>{fmtDec(w.avg_days_gen_to_invoice)}</td>
                    <td className={w.over_2_days > 0 ? 'wp-td-alert' : ''}>{fmt(w.over_2_days)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default WarehouseProductivityHybrid;
